// pages/DwiSendApprovals.jsx
// /dwisend-approvals — DwiSend offline payments waiting for approval
// (two-person check: one person records, a DIFFERENT approver approves with a
// code from their authenticator app). For admins and payment approvers; an
// approver need not be an admin and sees only this. Also shown in
// Admin → DwiSend → Approvals. Records only — approving moves no money.
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { dwisendPaymentAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Button, Card, Input, Badge, Modal, Empty, Spinner, SectionHeader } from '../components/ui';

const TABS = [['pending_approval', 'Waiting'], ['paid', 'Paid'], ['rejected', 'Rejected']];
const STATUS_COLOR = { pending_approval: 'amber', paid: 'green', rejected: 'gray' };
const METHOD_LABEL = { bank: 'Bank', mobile_money: 'Mobile money', cash_deposit: 'Cash deposit', other: 'Other' };

function money(minor, currency) {
  try {
    const nf = new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'code' });
    return nf.format(minor / 10 ** nf.resolvedOptions().maximumFractionDigits);
  } catch { return `${currency} ${minor}`; }
}
const day = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export function ApprovalsPanel() {
  const { user } = useAuth();
  const [st, setSt]           = useState(null);
  const [blocked, setBlocked] = useState(null);   // { status, message } when the page can't be used
  const [tab, setTab]         = useState('pending_approval');
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  useEffect(() => {
    dwisendPaymentAPI.status().then(setSt).catch(err => setBlocked({ status: err.status, message: err.message }));
  }, []);

  const load = useCallback(async () => {
    if (!st || !st.enabled) return;   // switched off: nothing can be recorded or approved
    setLoading(true);
    try { setItems((await dwisendPaymentAPI.list(tab)).payments || []); }
    catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [st, tab]);
  useEffect(() => { load(); }, [load]);

  if (blocked) {
    return (
      <Card>
        <SectionHeader title="✅ DwiSend payment approvals" />
        <p className="text-sm text-text-secondary">
          {blocked.status === 503 ? 'DwiSend offline payments are switched off.'
            : blocked.status === 403 ? 'This page is for DwiSend payment approvers.'
            : blocked.message}
        </p>
      </Card>
    );
  }
  if (!st) return <div className="py-10 flex justify-center"><Spinner /></div>;

  const myId = String(user?._id || user?.id || '');
  // Switched off: the approver sees that they are an approver, and that there is nothing to approve yet.
  if (!st.enabled) {
    return (
      <Card>
        <SectionHeader title="✅ DwiSend payment approvals" />
        <p className="text-sm text-text-secondary">
          DwiSend payment approvals are switched off — there is nothing to approve yet.
          {st.youAreApprover ? ' You are a payment approver: when the owner switches approvals on, payments to check appear here.' : ''}
          <span className="block mt-1 text-text-muted">Les approbations de paiement DwiSend sont désactivées — rien à approuver pour le moment.</span>
        </p>
        {st.youAreApprover && !st.twoFactorReady && (
          <Banner color="amber">
            Set up two-step login before you can approve: <Link to="/settings" className="underline font-semibold">Settings → Two-Factor Authentication</Link>.
          </Banner>
        )}
      </Card>
    );
  }
  return (
    <Card>
      <SectionHeader title="✅ DwiSend payment approvals" />
      <p className="text-xs text-text-muted mb-3">
        One person records a payment; a <strong>different</strong> approver approves it after checking the money on the real
        bank / mobile-money statement — never a screenshot. Approving marks it paid; no money moves in the app.
      </p>
      {!st.approvalOpen && <Banner color="amber">{st.message}</Banner>}
      {st.youAreApprover && !st.twoFactorReady && (
        <Banner color="amber">
          Set up two-step login before you can approve: <Link to="/settings" className="underline font-semibold">Settings → Two-Factor Authentication</Link>.
          Approving needs a 6-digit code from your authenticator app.
        </Banner>
      )}
      <div className="flex gap-2 flex-wrap mb-4">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${tab === id
              ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'}`}>{label}</button>
        ))}
      </div>
      {loading ? <div className="py-10 flex justify-center"><Spinner /></div>
        : items.length === 0 ? <Empty title={tab === 'pending_approval' ? 'Nothing waiting for approval' : 'Nothing here yet'} />
        : (
          <div className="space-y-3">
            {items.map(p => {
              const iRecorded = String(p.recordedBy?._id || p.recordedBy) === myId;
              return (
                <div key={p._id} className="border border-border rounded-xl p-4 bg-bg-panel">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-text-primary font-bold">{money(p.amountMinor, p.currency)} <span className="text-text-muted font-normal text-xs">· {p.ref} · {p.countryCode}</span></div>
                      <div className="text-xs text-text-muted">For {p.subjectType} {p.subjectRef}</div>
                    </div>
                    <Badge label={TABS.find(t => t[0] === p.status)?.[1] || p.status} color={STATUS_COLOR[p.status] || 'gray'} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-xs">
                    <Row k="Payer (as on statement)" v={p.payerName + (p.payerPhone ? ` · ${p.payerPhone}` : '')} />
                    <Row k="Method" v={METHOD_LABEL[p.method] || p.method} />
                    <Row k="Transaction reference" v={p.providerRef} mono />
                    <Row k="Received into" v={p.receivedInto} />
                    <Row k="Date on statement" v={day(p.paidOn)} />
                    <Row k="Recorded by" v={`${p.recordedBy?.name || '—'} · ${when(p.createdAt)}`} />
                    {p.proofNote && <Row k="Proof note" v={p.proofNote} />}
                    {p.status === 'paid' && <Row k="Approved by" v={`${p.approvals?.[0]?.approverId?.name || '—'} · ${when(p.paidAt)}${p.approvals?.[0]?.note ? ` — ${p.approvals[0].note}` : ''}`} />}
                    {p.status === 'rejected' && <Row k="Rejected by" v={`${p.rejectedBy?.name || '—'} · ${p.rejectReason}`} />}
                  </div>
                  {p.status === 'pending_approval' && st.youAreApprover && (
                    <div className="flex gap-2 mt-3 items-center flex-wrap">
                      {iRecorded
                        ? <span className="text-xs text-text-muted">You recorded this — a different approver must approve it.</span>
                        : <Button size="sm" disabled={!st.twoFactorReady || !st.approvalOpen} onClick={() => setApproving(p)}>Approve</Button>}
                      <Button size="sm" variant="ghost" onClick={() => setRejecting(p)}>Reject</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      {approving && <ApproveModal p={approving} onClose={() => setApproving(null)} onDone={() => { setApproving(null); load(); }} />}
      {rejecting && <RejectModal p={rejecting} onClose={() => setRejecting(null)} onDone={() => { setRejecting(null); load(); }} />}
    </Card>
  );
}

function Banner({ color, children }) {
  const cls = color === 'amber' ? 'bg-amber/10 border-amber/25 text-amber-400' : 'bg-bg-panel border-border text-text-secondary';
  return <div className={`border rounded-xl px-3 py-2 text-xs mb-3 ${cls}`}>{children}</div>;
}

function Row({ k, v, mono }) {
  return (
    <div className="flex gap-2 min-w-0">
      <span className="text-text-muted shrink-0">{k}:</span>
      <span className={`text-text-primary break-all ${mono ? 'font-mono' : ''}`}>{v || '—'}</span>
    </div>
  );
}

function ApproveModal({ p, onClose, onDone }) {
  const [checked, setChecked] = useState(false);
  const [code, setCode]       = useState('');
  const [note, setNote]       = useState('');
  const [saving, setSaving]   = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await dwisendPaymentAPI.approve(p._id, { confirmed: checked, code: code.trim(), note: note.trim() });
      toast.success(r.message || 'Approved');
      onDone();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Approve ${p.ref} — ${money(p.amountMinor, p.currency)}`} size="sm">
      <div className="space-y-3">
        <label className="flex gap-2 items-start text-sm text-text-secondary cursor-pointer">
          <input type="checkbox" className="mt-1" checked={checked} onChange={e => setChecked(e.target.checked)} />
          <span>I checked the real statement for <strong>{p.receivedInto}</strong>: {money(p.amountMinor, p.currency)} from {p.payerName},
            reference <span className="font-mono">{p.providerRef}</span>, arrived. Not a screenshot.</span>
        </label>
        <Input label="Code from your authenticator app" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
          placeholder="123456" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
        <Input label="Note (optional)" placeholder="e.g. seen on Equity statement 24/10" value={note} onChange={e => setNote(e.target.value)} />
        <div className="flex gap-2">
          <Button onClick={save} loading={saving} disabled={!checked || code.length !== 6}>Approve — mark paid</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
}

function RejectModal({ p, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await dwisendPaymentAPI.reject(p._id, reason.trim()); toast.success(`${p.ref} rejected`); onDone(); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Reject ${p.ref}`} size="sm">
      <div className="space-y-3">
        <Input label="Why?" placeholder="e.g. not on the statement, wrong amount" value={reason} onChange={e => setReason(e.target.value)} />
        <div className="flex gap-2">
          <Button onClick={save} loading={saving} disabled={!reason.trim()}>Reject</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
}

export default function DwiSendApprovals() {
  return <div className="max-w-4xl mx-auto"><ApprovalsPanel /></div>;
}
